import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMassagerAuthContext } from '@shared/providers/MassagerAuthContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { listMassagerClinicLocations } from '@/services/massager/clinicLocation.service';
import type { MassagerClinicLocationListResponse } from '@/types/massager/clinicLocation';

interface UseMassagerClinicLocationsArgs {
  /** YYYY-MM-DD — date the massager must be active on. */
  date: string | null;
  /** Theme service type ('A2' or 'R2'). null = no service filter. */
  serviceType: 'A2' | 'R2' | null;
}

/**
 * Locations the logged-in massager is registered to and active on `date`,
 * filtered by `serviceType`. Powers the 施設で絞り込み dropdown.
 */
export function useMassagerClinicLocations({
  date,
  serviceType,
}: UseMassagerClinicLocationsArgs) {
  const { clinic } = useMassagerAuthContext();
  const toast = useAppToast();

  const siteType = clinic?.site_type ?? '';

  const query = useQuery<MassagerClinicLocationListResponse, Error>({
    queryKey: ['massager', 'clinic-locations', siteType, date, serviceType],
    queryFn: () =>
      listMassagerClinicLocations(siteType, {
        date: date!,
        serviceType,
      }),
    enabled: !!siteType && !!date,
    placeholderData: prev => prev,
  });

  useEffect(() => {
    if (query.isError) {
      toast.error(getApiErrorMessage(query.error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  return {
    locations: query.data?.data?.clinic_locations ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
}
